import { clearAuthState, getStoredAuth, setStoredAuth } from 'auth/sessionManager';
import appConfig from 'configs/app.config';
import { auth, createUserWithEmailAndPassword, facebookProvider, googleProvider, signInWithPopup } from 'firebase.js';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import { AccountSignIn } from 'services/AuthenticationService';
import { apiGetMe, apiSignOut } from 'services/AuthService';
import { getClientData, onSignInSuccess, setClientId } from 'store/auth/sessionSlice';
import { setUser } from 'store/auth/userSlice';

function useAuth() {

    const dispatch = useDispatch()
    const navigate = useNavigate()

    const session = useSelector((state) => state.auth.session)

    const persistUser = (user) => {
        const userPayload = {
            id: user._id,
            userName: user.email,
            firstName: user.firstName,
            lastName: user.lastName,
            authority: ['admin'],
            role: user.userType,
            clientId: user.client || null,
        }

        dispatch(setUser(userPayload))
        localStorage.setItem('User_Info', JSON.stringify(user._id))
        localStorage.setItem('User_Full_Info', JSON.stringify(user))

        dispatch(getClientData({ id: user._id }))
        if (user.client?._id) {
            dispatch(setClientId(user.client._id))
        }
    }

    const signIn = async ({ userName, password }) => {
        try {
            const resp = await AccountSignIn(userName, password)
            if (resp.status && resp.accessToken && resp.refreshToken && resp.sessionId) {
                const { user, accessToken, refreshToken, sessionId } = resp

                setStoredAuth({ accessToken, refreshToken, sessionId })
                persistUser(user)

                navigate('/project/dashboard')

                return { status: 'success', message: '' }
            }

            return {
                status: 'failed',
                message: resp.message,
            }
        } catch (error) {
            return {
                status: 'failed',
                message: error?.response?.data?.message || error.toString(),
            }
        }
    }

    const googleSignIn = async () => {
        try {
            await signInWithPopup(auth, googleProvider)
            return { status: 'success' }
        } catch (error) {
            return { status: 'failed', message: error.message }
        }
    }

    const facebookSignIn = async () => {
        try {
            await signInWithPopup(auth, facebookProvider)
            return { status: 'success' }
        } catch (error) {
            return { status: 'failed', message: error.message }
        }
    }

    const handleSignOut = async () => {
        await clearAuthState()
        navigate(appConfig.unAuthenticatedEntryPath)
    }

    const signOut = async () => {
        try {
            await apiSignOut({ sessionId: session.sessionId || undefined })
            await handleSignOut()
        } catch (errors) {
            await handleSignOut()
        }
    }

    const bootstrapAuth = async () => {
        const { accessToken, refreshToken, sessionId } = getStoredAuth()
        if (!accessToken || !refreshToken || !sessionId) {
            await clearAuthState()
            return
        }

        dispatch(onSignInSuccess({ accessToken, refreshToken, sessionId }))

        try {
            const meResponse = await apiGetMe()
            if (meResponse?.status && meResponse?.user) {
                persistUser(meResponse.user)
                return
            }
            await clearAuthState()
        } catch (error) {
            if (error?.code === 'FORBIDDEN') {
                return
            }
			const backendCode = error?.response?.data?.code
            if (backendCode) {
                return
            }

            await clearAuthState()
        }
    }

    return {
        authenticated: Boolean(session.accessToken && session.signedIn),
        signIn,
        signOut,
        bootstrapAuth,
        googleSignIn,
        facebookSignIn,
        createUserWithEmailAndPassword,
    }
}

export default useAuth